import { useEffect, useRef, useState } from 'react'
import { FunctionsHttpError } from '@supabase/supabase-js'
import type { AggregatedGroceryItem } from './aggregateGroceryList'
import { chainToStoreSlug, STORE_DISPLAY_NAMES, toBackendQuality, type QualityPreference } from './groceryPrices'
import { supabase } from './supabaseClient'
import type { NearbyStore } from './storeTypes'

async function readFunctionError(error: unknown): Promise<string | null> {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = (await error.context.json()) as { error?: string; message?: string }
      if (payload?.error) return payload.error
      if (payload?.message) return payload.message
    } catch {
      /* ignore parse failure */
    }
  }
  if (error instanceof Error && error.message) return error.message
  return null
}

export type CartLineItem = {
  ingredient: string
  product_name: string
  packs: number
  pack_label: string
  unit_price: number
  line_total: number
  is_bio: boolean
  organic_fallback: boolean
  matched: boolean
  store: string
  category: string
  price_unavailable?: boolean
}

export type OptimizedStoreCart = {
  store: string
  store_display: string
  total_cost: number
  estimated_total: number
  matched_total: number
  missing_items_count: number
  items_found_count: number
  distance_km: number | null
  items: CartLineItem[]
  is_best_value: boolean
}

export type CartPricesResult = {
  carts: OptimizedStoreCart[]
  loading: boolean
  error: string | null
}

export function useCartPrices(
  aggregatedItems: AggregatedGroceryItem[],
  qualityPref: QualityPreference,
  isPremium: boolean,
  nearbyStores: NearbyStore[] = [],
  countryCode = 'AT',
  currencyCode = 'EUR',
): CartPricesResult {
  const [carts, setCarts] = useState<OptimizedStoreCart[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestIdRef = useRef(0)

  const storeChains = nearbyStores.map((s) => chainToStoreSlug(s.chain))

  useEffect(() => {
    if (!isPremium || aggregatedItems.length === 0) {
      setCarts([])
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)
    setError(null)

    async function fetchCarts() {
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('get-store-carts', {
          body: {
            ingredients: aggregatedItems.map((item) => ({
              name: item.canonicalName,
              quantity_mass: item.quantity_mass,
              shopping_pack_amount: item.shopping_pack?.pack_amount,
              shopping_pack_unit: item.shopping_pack?.pack_unit,
              packs_needed: item.shopping_pack?.packs_needed,
            })),
            quality_preference: toBackendQuality(qualityPref),
            store_chains: storeChains.length > 0 ? [...new Set(storeChains)] : undefined,
            country_code: countryCode.toUpperCase(),
            currency_code: currencyCode.toUpperCase(),
          },
        })

        if (requestId !== requestIdRef.current) return

        if (invokeError) {
          const detail = await readFunctionError(invokeError)
          setError(detail ?? 'Failed to fetch store prices')
          setCarts([])
          setLoading(false)
          return
        }

        if (data?.error) {
          setError(String(data.error))
          setCarts([])
          setLoading(false)
          return
        }

        const enriched = ((data?.carts ?? []) as OptimizedStoreCart[]).map((cart) => ({
          ...cart,
          estimated_total: cart.estimated_total ?? 0,
          matched_total: cart.matched_total ?? cart.total_cost,
          store_display: STORE_DISPLAY_NAMES[cart.store] ?? cart.store.toUpperCase(),
        }))

        setCarts(enriched)
        setLoading(false)
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        console.error('useCartPrices error:', err)
        setError('Price sync failed. Showing estimates.')
        setCarts([])
        setLoading(false)
      }
    }

    void fetchCarts()
  }, [isPremium, aggregatedItems, qualityPref, storeChains.join(','), countryCode, currencyCode])

  return { carts, loading, error }
}
